import { DataField } from './DataModels';
import { makeBooleanDataField, makeInt64DataField, makeStringDataField } from './DataFieldHelper';

function getFullFieldName(instanceName: string | undefined, fieldName: string) {
  return instanceName ? instanceName + '.' + fieldName : fieldName;
}

/**
 * Adds a data field from a contract to the data fields array.
 * If the data field value is empty, the field is skipped.
 * @param instanceName - The name of the contract instance. Empty if no instance.
 * @param fieldName - The name of the field ot add
 * @param value - The value
 * @param dataFields - The array of data fields to which a field will be added
 */
function addStringDataField(
  instanceName: string | undefined,
  fieldName: string,
  value: string | undefined | null,
  dataFields: DataField[]
) {
  if (value) {
    dataFields.push(makeStringDataField(`${getFullFieldName(instanceName, fieldName)}`, value));
  }
}
function addBooleanDataField(
  instanceName: string | undefined,
  fieldName: string,
  value: boolean | undefined | null,
  dataFields: DataField[]
) {
  if (typeof value === 'boolean') {
    dataFields.push(makeBooleanDataField(`${getFullFieldName(instanceName, fieldName)}`, value));
  }
}
function addInt64DataField(instanceName: string | undefined, fieldName: string, value: number | undefined | null, dataFields: DataField[]) {
  if (typeof value === 'number') {
    dataFields.push(makeInt64DataField(`${getFullFieldName(instanceName, fieldName)}`, value));
  }
}

export namespace Result {
  export interface Contract {
    code: number;
    // tslint:disable-next-line:no-reserved-keywords
    type?: string;
    tag?: number;
    isExpected?: boolean;
  }
}
export const Result = {
  /* Gets data fields for the Office.System.Result contract */
  getFields(contract: Result.Contract): DataField[] {
    const instanceName = 'Activity.Result';
    const contractName = 'Office.System.Result';
    let dataFields: DataField[] = [];
    addInt64DataField(instanceName, 'Code', contract.code, dataFields);
    addStringDataField(instanceName, 'Type', contract.type, dataFields);
    addInt64DataField(instanceName, 'Tag', contract.tag, dataFields);
    addBooleanDataField(instanceName, 'IsExpected', contract.isExpected, dataFields);
    dataFields.push(makeStringDataField('zC.Activity.Result', contractName));
    return dataFields;
  }
}; // Result

export namespace Activity {
  export interface Contract {
    cV?: string;
    duration: number;
    count: number;
    aggMode: number;
    success?: boolean;
    result?: Result.Contract;
  }
}
export const Activity = {
  contractName: 'Office.System.Activity',
  /* Gets data fields for the Office.System.Activity contract */
  getFields(contract: Activity.Contract): DataField[] {
    const instanceName = 'Activity';
    let dataFields: DataField[] = [];
    addStringDataField(instanceName, 'CV', contract.cV, dataFields);
    addInt64DataField(instanceName, 'Duration', contract.duration, dataFields);
    addInt64DataField(instanceName, 'Count', contract.count, dataFields);
    addInt64DataField(instanceName, 'AggMode', contract.aggMode, dataFields);
    addBooleanDataField(instanceName, 'Success', contract.success, dataFields);
    if (contract.result) {
      dataFields.push(...Result.getFields(contract.result));
    }
    dataFields.push(makeStringDataField('zC.Activity', this.contractName));
    return dataFields;
  }
}; // Activity

export namespace User {
  // 'Office.System.User'
  export interface Contract {
    primaryIdentityHash?: string;
    primaryIdentitySpace?: string;
    tenantId?: string;
    tenantGroup?: string;
    isAnonymous?: boolean;
  }
}
export const User = {
  /* Gets data fields for the Office.System.User contract */
  getFields(contract: User.Contract): DataField[] {
    const instanceName = 'User';
    let dataFields: DataField[] = [];
    addStringDataField(instanceName, 'PrimaryIdentityHash', contract.primaryIdentityHash, dataFields);
    addStringDataField(instanceName, 'PrimaryIdentitySpace', contract.primaryIdentitySpace, dataFields);
    addStringDataField(instanceName, 'TenantId', contract.tenantId, dataFields);
    addStringDataField(instanceName, 'TenantGroup', contract.tenantGroup, dataFields);
    addBooleanDataField(instanceName, 'IsAnonymous', contract.isAnonymous, dataFields);
    return dataFields;
  }
}; // User

export namespace Funnel {
  // 'Office.System.Funnel'
  export interface Contract {
    name?: string;
    state?: string;
  }
}
export const Funnel = {
  /* Gets data fields for the Office.System.Funnel contract */
  getFields(instanceName: string, contract: Funnel.Contract): DataField[] {
    let dataFields: DataField[] = [];
    addStringDataField(instanceName, 'Name', contract.name, dataFields);
    addStringDataField(instanceName, 'State', contract.state, dataFields);
    return dataFields;
  }
}; // Funnel

export namespace UserAction {
  // 'Office.System.UserAction'
  export interface Contract {
    id?: number;
    name?: string;
    commandSurface?: string;
    parentName?: string;
    triggerMethod?: string;
    timeOffsetMs?: number;
  }
}
export const UserAction = {
  /* Gets data fields for the Office.System.UserAction contract */
  getFields(contract: UserAction.Contract): DataField[] {
    const instanceName = 'UserAction';
    let dataFields: DataField[] = [];
    addInt64DataField(instanceName, 'Id', contract.id, dataFields);
    addStringDataField(instanceName, 'Name', contract.name, dataFields);
    addStringDataField(instanceName, 'CommandSurface', contract.commandSurface, dataFields);
    addStringDataField(instanceName, 'ParentName', contract.parentName, dataFields);
    addStringDataField(instanceName, 'TriggerMethod', contract.triggerMethod, dataFields);
    addInt64DataField(instanceName, 'TimeOffsetMs', contract.timeOffsetMs, dataFields);
    return dataFields;
  }
}; // UserAction

export namespace App {
  // 'Office.System.App'
  export interface Contract {
    name?: string;
    platform?: string;
    version?: string;
  }
}
export const App = {
  /* Gets data fields for the Office.System.App contract */
  getFields(contract: App.Contract): DataField[] {
    const instanceName = 'App';
    let dataFields: DataField[] = [];
    addStringDataField(instanceName, 'Name', contract.name, dataFields);
    addStringDataField(instanceName, 'Platform', contract.platform, dataFields);
    addStringDataField(instanceName, 'Version', contract.version, dataFields);
    return dataFields;
  }
}; // App

export namespace Session {
  // 'Office.System.Session'
  export interface Contract {
    id: string;
  }
}
export const Session = {
  /* Gets data fields for the Office.System.Session contract */
  getFields(contract: Session.Contract): DataField[] {
    const instanceName = 'Session';
    let dataFields: DataField[] = [];
    addStringDataField(instanceName, 'Id', contract.id, dataFields);
    return dataFields;
  }
}; // Session

// See https://aka.ms/asha/docs for documentation on usage and parameters.
// The HealthQosError 'contract' is a set of fields, but it does not
// have an instance name; thus, fields are sent as Data.Pillar instead of
// Data.HealthQosError.Pillar
export namespace HealthQosError {
  export interface Contract {
    pillar: QosPillarName;
    veto: string;
    isIntentional: boolean;
    isInternal: boolean;
    alertOnPillar: boolean;
    isSessionEndingError: boolean;
    inStaging?: boolean;
  }
}
export const enum QosPillarName {
  Interruptions = 'Interruptions',
  Open = 'Open',
  Save = 'Save',
  RealTimeCoauth = 'RealTimeCoauth',
  NotebookNavigation = 'NotebookNavigation',
  Fidelity = 'Fidelity',
  AsyncCollab = 'AsyncCollab',
  Present = 'Present',
  ContentConsumption = 'ContentConsumption',
  ContentCreation = 'ContentCreation'
}
export const HealthQosError = {
  /**
   * Gets data fields for the Health contract
   * See https://aka.ms/asha/docs for documentation on usage and parameters.
   * Generally, use sendHealthQosError() instead of using this contract directly.
   */
  getFields(contract: HealthQosError.Contract): DataField[] {
    let dataFields: DataField[] = [];
    dataFields.push(makeStringDataField('Pillar', contract.pillar));
    dataFields.push(makeStringDataField('Veto', contract.veto));
    dataFields.push(makeBooleanDataField('IsIntentional', contract.isIntentional));
    dataFields.push(makeBooleanDataField('IsInternal', contract.isInternal));
    dataFields.push(makeBooleanDataField('AlertOnPillar', contract.alertOnPillar));
    dataFields.push(makeBooleanDataField('IsSessionEndingError', contract.isSessionEndingError));
    addBooleanDataField(undefined, 'InStaging', contract.inStaging, dataFields);
    return dataFields;
  }
}; // HealthQosError
